import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { onCaptureBatch } from '../lib/extensionBridge';
import type { CaptureIngestOptions } from '../lib/captureIngest';
import i18next from 'i18next';
import { limit } from '../lib/entitlements';
import { importSealKey } from '../lib/seal';
import { useSessionStore } from '../stores/sessionStore';
import { useUiStore } from '../stores/uiStore';

/** Liga o canal da extensão ao pipeline local e só devolve sucesso após a confirmação local. */
export function useCaptureIngest(options?: CaptureIngestOptions): void {
  const queryClient = useQueryClient();
  const optionsRef = useRef(options);
  optionsRef.current = options;

  useEffect(() => {
    return onCaptureBatch(async (batch, { ackId }) => {
      // M1: mantém normalização, validações e libphonenumber fora do chunk principal.
      // O handler aguarda o módulo antes de processar, preservando o ACK pós-commit.
      const { captureIngestFailure, ingestCaptureBatch } = await import('../lib/captureIngest');
      try {
        // Teto de leads por lista do plano (teste: 50; decisão 290).
        const session = useSessionStore.getState().session;
        const maxRecords = limit(session, 'capture.results_max');
        // Leitura limitada (teste: 5 por lista): o resto é fechado com a chave do servidor
        // (decisão 293). Sem a chave no `/me`, fica como antes, embaralhado só na tela.
        const readableMax = limit(session, 'lists.readable_max');
        const sealKey = session?.entitlements?.sealKey;
        const seal =
          readableMax !== null && sealKey
            ? { readableMax, publicKey: await importSealKey(sealKey) }
            : null;
        const result = await ingestCaptureBatch(batch, {
          ...optionsRef.current,
          maxRecords,
          seal,
        });
        if (result.discarded === 'stale_run') {
          // ACK positivo encerra o retry do envelope antigo; nenhum registro ou cursor mudou.
          return { ok: true, inserted: 0, count: result.count };
        }
        const { handleStoredCaptureBatch } = await import('../lib/captureRuntime');
        await handleStoredCaptureBatch({ ackId, batch, result });
        if (result.full && maxRecords !== null) {
          // Chegou ao teto: a captura para aqui, e a pessoa sabe por quê.
          const { stopCaptureRuntime } = await import('../lib/captureRuntime');
          await stopCaptureRuntime(batch.listId).catch(() => false);
          useUiStore.getState().pushNotice(
            'warning',
            i18next.t('capture_results_limit', {
              n: maxRecords,
              defaultValue:
                'A lista chegou a {{n}} leads, o limite do teste. A captura parou aqui.',
            }),
          );
        }
        void queryClient.invalidateQueries({ queryKey: ['lists', batch.module] });
        void queryClient.invalidateQueries({
          queryKey: [batch.module === 'places' ? 'places' : 'leads', batch.listId],
        });
        return { ok: true, inserted: result.inserted, count: result.count };
      } catch (error) {
        const failure = captureIngestFailure(error);
        if (failure.error === 'email_dns_unavailable' || failure.error === 'quota_exceeded') {
          try {
            const { handleCaptureIngestFailure } = await import('../lib/captureRuntime');
            await handleCaptureIngestFailure(batch, failure.error);
          } catch {
            // O ACK precisa manter o erro original mesmo se a UI da pausa não puder persistir.
          }
        }
        return failure;
      }
    });
  }, [queryClient]);
}
